import {
  Component,
  Inject,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ElementRef
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl
} from "@angular/forms";
import { DocumentCategoryService } from "../../../../service/repository/document-category.service";
import { DocumentService } from "../../../../service/repository/document.service";
import { GlobalAppService } from "../../../../../core/commons/global-app.service";
import { Document as Doc } from "../../../../domain/document";
import { MessageSeverity } from "../../../../../core/commons/message-severity";
import { User } from "../../../../../core/domain/user";
import {
  MatChipInputEvent,
  MatAutocomplete,
  MatAutocompleteSelectedEvent
} from "@angular/material";
import { ConfirmationService } from "primeng/primeng";
import { DocumentCategoryView } from "../../../../domain/documentCategoryView";
import {
  animate,
  state,
  style,
  transition,
  trigger
} from "@angular/animations";
import { DocumentCategory } from "../../../../domain/documentCategory";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { PdfViewerComponent } from "../pdf-viewer/pdf-viewer.component";
import { Keyword } from "app/fiscalia/domain/keyword";

@Component({
  selector: "document-form",
  templateUrl: "./document-form.component.html",
  styleUrls: ["./document-form.component.scss"],
  animations: [
    trigger("showDetail", [
      state("void", style({ right: "-170px" })),
      state("hide", style({ right: "-170px" })),
      state("show", style({ right: "0" })),
      transition("* => show", [animate("195ms ease-in-out")]),
      transition("show => hide", [animate("140ms ease-in-out")])
    ])
  ]
})
export class DocumentFormComponent implements OnInit {
  documentForm: FormGroup = null;
  documentSelected: Doc = null;

  @Input() create: boolean = false;
  @Input() categorySelected: DocumentCategoryView = null;

  @Output() onDocumentDeleted: EventEmitter<string> = new EventEmitter();
  @Output() onDocumentChange: EventEmitter<string> = new EventEmitter();
  @Output() onFormClose: EventEmitter<string> = new EventEmitter();

  fileName: string = ""; // Nombre del archivo que se va a subir.
  showDetail = true;
  //formState = "hide"; //Estado del detalle de un documento que fue seleccionado, "hide" => "show".
  newFile: boolean = false;
  title: string = "";
  file: File = null;
  user: User;

  // KeyWords
  keywords: string[] = [];
  searchCtrl = new FormControl();
  filteredOptions: Keyword[];
  @ViewChild("searchInput") searchInput;

  constructor(
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private categoryService: DocumentCategoryService,
    private documentService: DocumentService,
    private messageService: GlobalAppService,
    private confirmationService: ConfirmationService
  ) {
    this.documentForm = this.formBuilder.group({
      id: [null],
      name: ["", [Validators.required, Validators.minLength(4)]],
      description: ["", [Validators.required, Validators.minLength(4)]],
      author: [null],
      keywords: [, Validators.required],
      createDate: [null],
      category: [null],
      uploadUser: [null]
    });
  }

  ngOnInit() {
    this.categoryService.authService.user.subscribe(u => (this.user = u));
    this.searchCtrl.valueChanges.debounceTime(400).subscribe(() => {
      this.applyFilter();
    });
  }

  @Input("document")
  set setDocument(d: Doc) {
    if (d && !this.create) {
      this.documentSelected = new Doc(Object.assign({}, d));
      this.setDocumentForm(d);
      this.showDetail = true;
    } else {
      this.documentSelected = new Doc();
      this.clearDocumentForm();
      this.showDetail = false;
      this.keywords = [];
    }
  }

  public deleteDocument() {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar el documento?",
      icon: "fa ui-icon-delete-forever",
      accept: () => {
        this.documentService.delete(this.documentSelected).subscribe(
          res => {
            this.onDocumentDeleted.emit("document deleted");
            this.messageService.sendMsg(
              MessageSeverity.SUCCESS,
              "Eliminado",
              "Se ha eliminado el documento."
            );
          },
          error => {
            this.messageService.sendMsg(
              MessageSeverity.ERROR,
              "Error",
              "No ha sido posible eliminar el documento."
            );
          }
        );
      }
    });
  }

  public downloadDocument() {
    this.documentService
      .downloadPDF(this.documentSelected.id.toString())
      .subscribe(res => {
        let fileURL = URL.createObjectURL(res);
        let downloadLink = document.createElement("a");
        downloadLink.href = fileURL;
        downloadLink.download = this.documentSelected.getFileName();
        downloadLink.click();
      });
  }

  public viewDocument() {
    this.messageService.setLoading(true);
    this.documentService
      .downloadPDF(this.documentSelected.id.toString())
      .subscribe(res => {
        let fileURL = URL.createObjectURL(res);
        this.openDialog(fileURL);
      });
  }

  openDialog(fileUrl: string): void {
    const dialogRef = this.dialog
      .open(PdfViewerComponent, {
        width: "100 %",
        height: "100 %",
        data: { name: this.documentSelected.name, pdf: fileUrl, pdfLink: fileUrl }
      })
      .afterOpen()
      .subscribe(res => this.messageService.setLoading(false));
  }

  /**
   * Toma toda la información del documento seleccionado para la edición y lo carga en el formulario.
   */
  public setDocumentForm(d: Doc): void {
    this.documentForm.get("id").setValue(d.id);
    this.documentForm.get("name").setValue(d.name);
    this.documentForm.get("author").setValue(d.author);
    this.documentForm.get("description").setValue(d.description);
    this.documentForm.get("category").setValue(d.category);
    this.documentForm.get("keywords").setValue(d.keywords);
    this.keywords = d.keywords.length > 0 ? d.keywords : [];
    this.documentForm.get("createDate").setValue(d.createDate);
    this.documentForm.get("uploadUser").setValue(d.uploadUser);
    this.fileName = d.getFileName();
  }

  /**
   * Limpia todos los campos del formulario.
   */
  public clearDocumentForm(): void {
    this.documentForm.reset();
    this.newFile = false;
  }

  /**
   * Valida si el documento es valido, dependiendo si es una modificación de un documento o uno nuevo.
   * @returns {boolean}
   */
  public validDocument(): boolean {
    if (this.create && this.newFile) return true;
    else if (!this.create) return true;

    return false;
  }

  public submit({ value, valid }) {
    if (valid) {
      let d = new Doc(value);
      d.uploadUser = Object.assign({},this.user,{
        fxs: null,
        roles: null,
        fxsUpdated:null,
        created:null
      });
      this.messageService.setLoading(true);
      if (this.create) {
        d.createDate = new Date().getTime();

        let c = new DocumentCategoryView(this.categorySelected);
        c.parentCategory = null;
        c.subCategories = null;
        d.category = c;

        this.documentService.create(d).subscribe(d => {
          this.messageService.sendMsg(
            MessageSeverity.SUCCESS,
            "Exito!",
            "El documento fue creo con exito."
          );
          this.documentService.uploadPDF(this.file, d.id.toString()).subscribe(
            res => {
              this.messageService.setLoading(false);
              this.onDocumentChange.emit("Document created");
            },
            error => console.log(error)
          );
        });
      } else {
        this.documentService.update(d).subscribe(d => {
          if (this.newFile) {
            this.documentService
              .uploadPDF(this.file, d.id.toString())
              .subscribe(
                res => {
                  this.messageService.setLoading(false);
                  this.onDocumentChange.emit("Document updated");
                },
                error => console.log(error)
              );
          } else {
            this.messageService.setLoading(false);
            this.onDocumentChange.emit("Document updated");
          }
          this.messageService.sendMsg(
            MessageSeverity.SUCCESS,
            "Documento modificado",
            "El documento se modifico con exito."
          );
        });
      }
    }
  }

  // keywords
  addKeyWord(event: MatChipInputEvent): void {
    let input = event.input;
    let value = event.value;

    // Add our person
    if ((value || "").trim()) {
      this.keywords.push(value.trim());
      this.documentForm.get("keywords").setValue(this.keywords);
    }
    // Reset the input value
    if (input) {
      input.value = "";
    }
  }

  addKeyword(event: MatAutocompleteSelectedEvent): void {
    this.keywords.push(event.option.value);
    this.documentForm.get("keywords").setValue(this.keywords);
    this.searchInput.nativeElement.value = "";
  }

  removeKeyWord(key: string): void {
    let index = this.keywords.indexOf(key);
    if (index >= 0) {
      this.keywords.splice(index, 1);
      this.documentForm.get("keywords").setValue(this.keywords);
    }
  }

  applyFilter() {
    let keywordValue = this.searchCtrl.value;
    this.documentService.findBykeyWord(keywordValue).subscribe(data => {
      this.filteredOptions = data;
    });
  }
  // end keywords

  public closeForm() {
    if (this.create) {
      this.onFormClose.emit("form closed");
    } else {
      this.showDetail = true;
    }
  }

  public editDocument() {
    this.showDetail = false;
    this.create = false;
  }

  public newFileUpload(f: File): void {
    this.file = f;
    this.fileName = f.name + " " + this.humanFileSize(f.size);
    this.newFile = true;
  }

  public humanFileSize(size): string {
    let i = Math.floor(Math.log(size) / Math.log(1024));
    return (
      +(+size / Math.pow(1024, i)).toFixed(2) * 1 +
      " " +
      ["B", "kB", "MB", "GB", "TB"][i]
    );
  }

  /**
   * Cierra la pestaña con el detalle de un documento
   */
  public closeTab(): void {
    this.documentSelected = null;
    this.onFormClose.emit("Tab closed");
  }

  public moveDocument(): void {
    this.messageService.setLoading(true);

    this.documentSelected.category = new DocumentCategory({
      id: this.categorySelected.id
    });

    this.documentService.update(this.documentSelected).subscribe(d => {
      this.messageService.setLoading(false);
      this.onDocumentChange.emit("Document moved");
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/fiscalia/component/repository/explorer/document-form/document-form.component.ts